import { EventDispatcher, Vector3, Spherical, MathUtils } from "three";
import normalizeWheel from "normalize-wheel";
import { embedInput } from "../logic/embed_controls.js";
import { orbitNavigation, panNavigation, zoomNavigation } from "../logic/navigation.js";

export class NavigationControls extends EventDispatcher {
    constructor(camera, canvas) {
        super();
        this.object = camera;
        this.target = new Vector3();
        this.enabled = true;
        const previousTouchAction = canvas.style.touchAction;
        canvas.style.touchAction = "none";
        // The Viewer already uses this input path for Safari touch gestures and
        // embedded mouse navigation: left orbit, middle/Shift pan, right zoom.
        const input = embedInput(canvas);
        const subscriptions = ["orbit", "pan", "zoom"].map(type => input[type].subscribe(delta => this.navigate(type, delta)));
        const wheel = event => {
            if (!this.enabled) return;
            event.preventDefault();
            this.navigate("zoom", { deltaZoom: normalizeWheel(event).spinY });
        };
        canvas.addEventListener("wheel", wheel, { passive: false });
        this.dispose = () => {
            subscriptions.forEach(subscription => subscription.unsubscribe());
            canvas.removeEventListener("wheel", wheel);
            canvas.style.touchAction = previousTouchAction;
        };
    }

    navigate(type, delta) {
        if (!this.enabled) return;
        this.dispatchEvent({ type: "start" });
        const camera = this.object;
        const orbit = new Spherical().setFromVector3(camera.position.clone().sub(this.target));
        const state = { target: this.target, theta: orbit.theta, phi: orbit.phi, distance: orbit.radius,
            projectionMode: camera.isOrthographicCamera ? "orthographic" : "perspective",
            orthographicSize: camera.isOrthographicCamera ? (camera.top - camera.bottom) / (2 * camera.zoom) : 2 };
        if (type === "orbit") orbitNavigation(state, delta);
        else if (type === "pan") panNavigation(state, camera, delta);
        else zoomNavigation(state, delta);
        orbit.set(Math.max(0.1, state.distance), MathUtils.clamp(state.phi, 0.01, Math.PI - 0.01), state.theta);
        camera.position.copy(this.target).add(new Vector3().setFromSpherical(orbit));
        if (camera.isOrthographicCamera && type === "zoom") {
            camera.zoom = (camera.top - camera.bottom) / (2 * state.orthographicSize);
            camera.updateProjectionMatrix();
        }
        this.update();
    }

    update() {
        this.object.lookAt(this.target);
        this.object.updateMatrixWorld();
        this.dispatchEvent({ type: "change" });
    }
}
